import { Tooltip, Button, Grid } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import Spin from 'components/Spin';
import moment from 'moment';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedNumber } from 'react-intl';
import { useElite } from 'v2/hooks/useElite';

import type { EliteList } from 'v2/interfaces/elite';

import BlackFridayChallenge from './BlackFridayChallenge';
import { useStyles } from './styles';

const moreInfoElite = 'https://programaelite.eduzz.com/';

interface ILevel {
  name: string;
  label: string;
  valueToAchieve: number;
  step?: number;
  color?: string;
}

const Elite = memo(() => {
  const classes = useStyles();

  const [data, setData] = useState<EliteList>(null);
  const eliteListQuery = useElite();
  const { data: elite, error } = eliteListQuery;

  const producerGains = useMemo(() => data?.gains_producer || 0, [data]);
  const affiliateGains = useMemo(() => data?.gains_affiliate || 0, [data]);
  const totalGains = useMemo(() => producerGains + affiliateGains, [affiliateGains, producerGains]);

  const isLoading = useMemo(() => data === null && error === undefined, [data, error]);

  const producerLevels = useMemo<ILevel[]>(
    () => [
      { step: 0, name: 'White Belt', label: 'R$ 0.00', valueToAchieve: 0 },
      { step: 0, name: 'Red Belt', label: 'R$ 100.000', color: '--red', valueToAchieve: 100000 },
      { step: 0, name: 'Orange Belt', label: 'R$ 250.000', color: '--orange', valueToAchieve: 250000 },
      { step: 0, name: 'Green Belt', label: 'R$ 500.000', color: '--green', valueToAchieve: 500000 },
      { step: 0, name: 'Black Belt', label: 'R$ 1.000.000', color: '--black', valueToAchieve: 1000000 },
      { step: 1, name: '2º DAN', label: 'R$ 2.000.000', valueToAchieve: 2000000 },
      { step: 1, name: '3º DAN', label: 'R$ 3.000.000', valueToAchieve: 3000000 },
      { step: 1, name: '4º DAN', label: 'R$ 4.000.000', valueToAchieve: 4000000 },
      { step: 1, name: '5º DAN', label: 'R$ 5.000.000', valueToAchieve: 5000000 },
      { step: 1, name: '6º DAN', label: 'R$ 6.000.000', valueToAchieve: 6000000 },
      { step: 1, name: '7º DAN', label: 'R$ 7.000.000', valueToAchieve: 7000000 },
      { step: 1, name: '8º DAN', label: 'R$ 8.000.000', valueToAchieve: 8000000 },
      { step: 1, name: '9º DAN', label: 'R$ 9.000.000', valueToAchieve: 9000000 },
      { step: 1, name: 'Golden', label: 'R$ 10.000.000', valueToAchieve: 10000000 },
      { step: 2, name: '2º DAN', label: 'R$ 20.000.000', color: '--golden', valueToAchieve: 20000000 },
      { step: 2, name: '3º DAN', label: 'R$ 30.000.000', valueToAchieve: 30000000 },
      { step: 2, name: '4º DAN', label: 'R$ 40.000.000', valueToAchieve: 40000000 },
      { step: 2, name: '5º DAN', label: 'R$ 50.000.000', valueToAchieve: 50000000 },
      { step: 2, name: '6º DAN', label: 'R$ 60.000.000', valueToAchieve: 60000000 },
      { step: 2, name: '7º DAN', label: 'R$ 70.000.000', valueToAchieve: 70000000 },
      { step: 2, name: '8º DAN', label: 'R$ 80.000.000', valueToAchieve: 80000000 },
      { step: 2, name: '9º DAN', label: 'R$ 90.000.000', valueToAchieve: 90000000 },
      { step: 2, name: 'Sensei', label: 'R$ 100.000.000', valueToAchieve: 100000000 },
    ],
    []
  );

  const affiliateLevels = useMemo<ILevel[]>(
    () => [
      { name: 'Level 0', label: 'R$ 0.00', color: '--affiliate', valueToAchieve: 0 },
      { name: 'Level 10K', label: 'R$ 10.000', color: '--affiliate10', valueToAchieve: 10000 },
      { name: 'Level 50K', label: 'R$ 50.000', color: '--affiliate10', valueToAchieve: 50000 },
      { name: 'Level 100K', label: 'R$ 100.000', color: '--affiliate100', valueToAchieve: 100000 },
      { name: 'Level 500K', label: 'R$ 500.000', color: '--affiliate500', valueToAchieve: 500000 },
      { name: 'High Level', label: 'R$ 1000.000', color: '--affiliateHigh', valueToAchieve: 1000000 },
    ],
    []
  );

  const producerLevel = useMemo(() => {
    return [...producerLevels].reverse().find((item) => producerGains >= item.valueToAchieve);
  }, [producerGains, producerLevels]);

  const affiliateLevel = useMemo(() => {
    return [...affiliateLevels].reverse().find((item) => affiliateGains >= item.valueToAchieve);
  }, [affiliateGains, affiliateLevels]);

  const isGoldenBeltD2Eligible = useMemo(() => producerGains >= 20000000, [producerGains]);
  const isBlackBeltD2Eligible = useMemo(() => producerGains >= 2000000, [producerGains]);

  const producerJourneyVisibleStep = useMemo(() => {
    if (isGoldenBeltD2Eligible) return 2;

    if (isBlackBeltD2Eligible) return 1;

    return 0;
  }, [isBlackBeltD2Eligible, isGoldenBeltD2Eligible]);

  const checkIfStepIsVisible = (total: number, step: number, nextStep: number): string => {
    if (total >= step && total >= nextStep) return '--visible';

    if (total >= step && total < nextStep) return '--active';

    return '';
  };

  const handleJoinBlackFriday = useCallback(async () => {
    eliteListQuery.refetch();
  }, [eliteListQuery]);

  useEffect(() => {
    if (elite) {
      setData(elite);
    }
  }, [elite, error]);

  return (
    <div className={classes.elite}>
      {isLoading ? (
        <div style={{ width: '100%', textAlign: 'center' }}>
          <Spin />
        </div>
      ) : (
        <>
          <BlackFridayChallenge
            alreadyJoinedBlackFridayChallenge={data?.already_joined_blackfriday_challenge}
            handleJoinBlackFriday={handleJoinBlackFriday}
          />

          <div className={classes.eliteHead}>
            <Grid container spacing={2}>
              <Grid className={classes.eliteTitle} item xs={12}>
                JORNADA DO PRODUTOR
              </Grid>

              <Grid item xs={12} md={8}>
                <div
                  className={`
                    ${isBlackBeltD2Eligible ? classes.eliteSlidePremium : classes.eliteSlide}
                    ${isGoldenBeltD2Eligible ? '--golden' : producerLevel?.color || ''}
                  `}
                >
                  {producerLevels.map(
                    (item, index) =>
                      producerJourneyVisibleStep === item.step && (
                        <div
                          key={item.valueToAchieve}
                          className={`
                            ${isBlackBeltD2Eligible ? classes.eliteSlideItemPremium : classes.eliteSlideItem}
                            ${checkIfStepIsVisible(producerGains, item.valueToAchieve, producerLevels[index + 1]?.valueToAchieve || 1000000000)}
                          `}
                        >
                          <label>{item.name}</label>
                          <span>{item.label}</span>
                        </div>
                      )
                  )}
                </div>
              </Grid>

              <Grid item xs={12} md={4} />
            </Grid>
          </div>

          <div className={`${classes.eliteHead} --affiliate`}>
            <Grid container spacing={2}>
              <Grid className={classes.eliteTitle} item xs={12}>
                JORNADA DO AFILIADO
              </Grid>

              <Grid item xs={12} md={8}>
                <div className={`${classes.eliteSlide} ${affiliateLevel?.color || '--affiliate'}`}>
                  {affiliateLevels.map((item, index) => (
                    <div
                      key={item.valueToAchieve}
                      className={`
                        ${classes.eliteSlideItem}
                        ${checkIfStepIsVisible(affiliateGains, item.valueToAchieve, affiliateLevels[index + 1]?.valueToAchieve || 1000000000)}
                      `}
                    >
                      <label>{item.name}</label>
                      <span>{item.label}</span>
                    </div>
                  ))}
                </div>
              </Grid>

              <Grid item xs={12} md={4} />
            </Grid>
          </div>

          <div className={classes.badgesContainer}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={8} className={classes.order2Mobile}>
                <div className={classes.badges}>
                  {(data?.badges || []).map((badge) => (
                    <div key={badge.id} className={`${classes.badgeItem} ${badge.has_badge && '--active'}`}>
                      <Tooltip title={`${badge.hint}`} placement='top'>
                        <img
                          src={`/img/badges/${badge.badge_elite.concat(badge.has_plus ? '-plus' : '')}.svg`}
                          alt={`Badge ${badge.has_plus ? badge.name : ''}`}
                        />
                      </Tooltip>
                      <label>{badge.name}</label>
                      {badge?.qualified_at && <label className='secondary-text'>{moment(badge.qualified_at).format('L')}</label>}
                    </div>
                  ))}
                </div>
              </Grid>

              <Grid item xs={12} md={4} className={classes.order1Mobile}>
                <div className={classes.profile}>
                  <div className={classes.profileAvatar}>
                    <div className={classes.avatar} style={{ backgroundImage: `url(${localStorage.getItem('photo')})` }}></div>

                    <div className={classes.avatarBadge}>
                      {producerGains >= affiliateGains
                        ? data?.progress?.producer && (
                            <img
                              src={`/img/badges/${data.progress.producer.current.badge_elite.concat(
                                data?.progress?.producer?.current?.has_plus ? '-plus.svg' : '.svg'
                              )}`}
                              alt={`Badge ${data.progress.producer.current.name}`}
                            />
                          )
                        : data?.progress?.affiliate && (
                            <img
                              src={`/img/badges/${data.progress.affiliate.current.badge_elite.concat(
                                data?.progress?.affiliate?.current.has_plus ? '-plus.svg' : '.svg'
                              )}`}
                              alt={`Badge ${data?.progress?.affiliate?.current?.name || ''}`}
                            />
                          )}
                    </div>
                  </div>

                  <div className={classes.profileText}>
                    <label>Total de ganhos</label>

                    <span>
                      <FormattedNumber value={totalGains} format='BRL' />
                    </span>
                  </div>

                  <div className='m24' />

                  <div className={classes.profileText}>
                    <label>Ganhos como produtor / coprodutor</label>

                    <span>
                      <FormattedNumber value={producerGains} format='BRL' />
                    </span>
                  </div>

                  <div className='m24' />

                  <div className={classes.profileText}>
                    <label>Vendas como produtor / coprodutor*</label>

                    <span>{data?.sales?.producer || 0}</span>
                  </div>

                  <div className='m24' />

                  <div className={classes.profileText}>
                    <label>Ganhos como afiliado / coafiliado</label>

                    <span>
                      <FormattedNumber value={affiliateGains} format='BRL' />
                    </span>
                  </div>

                  <div className='m24' />

                  <div className={classes.profileText}>
                    <label>Vendas como afiliado* / coafiliado*</label>

                    <span>{data?.sales?.affiliate || 0}</span>
                  </div>

                  <div className='m24' />

                  <Typography className={classes.subTitle}>
                    * Caso você seja coprodutor e afiliado, ou coafiliado, em uma mesma venda, ela será contabilizada nos dois indicadores
                  </Typography>

                  <div className='m24' />

                  <div className={classes.knowMore}>
                    <Button variant='contained' color='primary' size='large' fullWidth onClick={() => window.open(moreInfoElite, '_blank')}>
                      Saiba mais sobre o programa Elite
                    </Button>
                  </div>
                </div>
              </Grid>
            </Grid>
          </div>
        </>
      )}
    </div>
  );
});

export default Elite;
